import { useMemo } from "react";

import { apiOutletClient } from "@/features/shared";

import { useAuth } from "./use-auth";

// Outlet-scoped API bound to an EXPLICIT outlet — the single sanctioned way to
// call outlet endpoints from feature/page code (never import apiOutletClient
// directly inside a form).
//
// Pass the form's outlet id (the seller / buyer / outlet field) so reads AND
// writes target that outlet, not the ambient global outlet. Omit outletId on
// org / current-outlet screens (lists, view pages) to use the signed-in current
// outlet from auth context. Either way the outlet is an explicit value the
// caller resolved — never a shared global read at request time.
export const useOutletApi = (outletId) => {
  const { outlet } = useAuth();
  const resolvedOutletId = outletId ?? outlet?.id ?? null;

  return useMemo(() => {
    const withOutletId = (config = {}) => {
      if (import.meta.env.DEV && !resolvedOutletId) {
        console.warn(
          "[useOutletApi] no outlet resolved (both the passed outletId and the current outlet are empty); the request will fall back to the tab's active outlet.",
        );
      }
      if (!resolvedOutletId) return config;
      return {
        ...config,
        headers: { "Outlet-Id": resolvedOutletId, ...config.headers },
      };
    };

    return {
      outletId: resolvedOutletId,
      get: (url, config) => apiOutletClient.get(url, withOutletId(config)),
      post: (url, data, config) =>
        apiOutletClient.post(url, data, withOutletId(config)),
      put: (url, data, config) =>
        apiOutletClient.put(url, data, withOutletId(config)),
      patch: (url, data, config) =>
        apiOutletClient.patch(url, data, withOutletId(config)),
      delete: (url, config) => apiOutletClient.delete(url, withOutletId(config)),
    };
  }, [resolvedOutletId]);
};
